/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ComponentType } from 'react';

import type { IconProps } from '@douyinfe/semi-icons';
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconClear,
  IconClose,
  IconCopyStroked,
  IconDeleteStroked,
  IconEditStroked,
  IconExpand,
  IconHistory,
  IconInbox,
  IconMinusStroked,
  IconMoreStroked,
  IconPlusStroked,
  IconRefresh,
  IconSearch,
  IconSetting,
  IconShrink,
  IconTick,
  IconUpload,
  IconUser,
  IconUserGroup,
  IconBulb,
  IconMoon,
  IconSun,
} from '@douyinfe/semi-icons';

export type AbrIconComponent = ComponentType<IconProps>;

/**
 * 爱波瑞 Agent 图标集（Semi 线性图标）。
 * 工作流顶栏 / Bot IDE / Project IDE 换肤时优先使用此集合。
 */
export const abrIcons = {
  ArrowLeft: IconChevronLeft,
  More: IconMoreStroked,
  Copy: IconCopyStroked,
  History: IconHistory,
  Setting: IconSetting,
  Plus: IconPlusStroked,
  Minus: IconMinusStroked,
  Upload: IconUpload,
  TrashCan: IconDeleteStroked,
  Expand: IconExpand,
  Minimize: IconShrink,
  Cross: IconClose,
  CrossFill: IconClose,
  InfoCircle: IconAlertCircle,
  WarningCircleFill: IconAlertCircle,
  CheckMarkFill: IconTick,
  CheckMarkCircleFillPalette: IconTick,
  ArrowDown: IconChevronDown,
  ArrowRight: IconChevronRight,
  ArrowRightFill: IconChevronRight,
  Apply: IconTick,
  Edit: IconEditStroked,
  People: IconUser,
  Team: IconUserGroup,
  Card: IconInbox,
  SideExpand: IconExpand,
  Refresh: IconRefresh,
  Search: IconSearch,
  Lightbulb: IconBulb,
  TrayArrowUp: IconUpload,
  Broom: IconClear,
  Moon: IconMoon,
  Sun: IconSun,
  Workflow: IconInbox,
  Plugin: IconSetting,
  Document: IconInbox,
  Variables: IconSetting,
  Chat: IconInbox,
} as const;

export type AbrIconName = keyof typeof abrIcons;

/**
 * IconCoz* 组件名 → abrIcons 键名对照表。
 * 页面换肤阶段按此表批量替换 import，底座阶段仅提供映射不改动业务文件。
 */
export const ABR_ICON_COZ_MAP = {
  IconCozArrowLeft: 'ArrowLeft',
  IconCozMore: 'More',
  IconCozCopy: 'Copy',
  IconCozHistory: 'History',
  IconCozSetting: 'Setting',
  IconCozPlus: 'Plus',
  IconCozMinus: 'Minus',
  IconCozUpload: 'Upload',
  IconCozTrashCan: 'TrashCan',
  IconCozExpand: 'Expand',
  IconCozMinimize: 'Minimize',
  IconCozCross: 'Cross',
  IconCozCrossFill: 'CrossFill',
  IconCozInfoCircle: 'InfoCircle',
  IconCozWarningCircleFill: 'WarningCircleFill',
  IconCozCheckMarkFill: 'CheckMarkFill',
  IconCozCheckMarkCircleFillPalette: 'CheckMarkCircleFillPalette',
  IconCozArrowDown: 'ArrowDown',
  IconCozArrowRight: 'ArrowRight',
  IconCozArrowRightFill: 'ArrowRightFill',
  IconCozApply: 'Apply',
  IconCozEdit: 'Edit',
  IconCozPeople: 'People',
  IconCozCard: 'Card',
  IconCozSideExpand: 'SideExpand',
  IconCozWorkflow: 'Workflow',
  IconCozPlugin: 'Plugin',
  IconCozDocument: 'Document',
  IconCozVariables: 'Variables',
  IconCozChat: 'Chat',
  IconCozLightbulb: 'Lightbulb',
  IconCozTrayArrowUp: 'TrayArrowUp',
  IconCozBroom: 'Broom',
  IconCozMoon: 'Moon',
  IconCozSun: 'Sun',
} as const satisfies Record<string, AbrIconName>;

export type AbrIconCozName = keyof typeof ABR_ICON_COZ_MAP;

/** 根据 IconCoz 组件名解析对应 Abrain 图标组件，未映射时返回 undefined */
export function resolveAbrIcon(cozIconName: AbrIconCozName): AbrIconComponent {
  const abrKey = ABR_ICON_COZ_MAP[cozIconName];
  return abrIcons[abrKey] as AbrIconComponent;
}
